import { useState } from 'react'
import {
  applyQuoteDiscount,
  discountLabel,
  normalizeDiscountInput,
  type QuoteDiscount,
} from '@limo/shared'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Percent, DollarSign } from 'lucide-react'

/**
 * ApplyDiscountDialog
 *
 * The single discount UI, shared by the quote detail page and the booking detail
 * page. Percent or flat, one active discount, same rules as the GoHighLevel
 * apply-quote-discount webhook (`normalizeDiscountInput` in @limo/shared).
 *
 * Purely presentational: the caller owns persistence, because a quote stores the
 * discount on its own row while a booking stores it as a booking_adjustments row.
 * The preview uses `applyQuoteDiscount` — the same helper checkout uses — so the
 * number shown here is the number that gets charged.
 */

interface ApplyDiscountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * The total with NO discount on it. Every percentage is measured against this,
   * so changing 10% to 5% re-bases instead of compounding on the already
   * discounted figure. Pricing-rule reductions (e.g. "Summer Discount (-20%)")
   * stay inside this number — they aren't discounts this dialog owns.
   */
  preDiscountTotal: number
  /** Existing discount, so the dialog opens prefilled and can offer removal. */
  currentDiscount?: QuoteDiscount | null
  /** Dollars the existing discount is worth today, for the "current total" line. */
  currentDiscountAmount?: number
  /** Noun used in the copy, e.g. "quote" or "booking". */
  entityLabel: string
  isPending?: boolean
  onApply: (discount: QuoteDiscount) => void
  onClear: () => void
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)

export function ApplyDiscountDialog({
  open,
  onOpenChange,
  ...formProps
}: ApplyDiscountDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        {/* The form lives in a child so it mounts fresh each time the dialog opens
            (Radix unmounts closed content). That's what seeds the inputs from the
            saved discount without an effect syncing props into state. */}
        <DiscountForm {...formProps} onCancel={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}

type DiscountFormProps = Omit<
  ApplyDiscountDialogProps,
  'open' | 'onOpenChange'
> & {
  onCancel: () => void
}

function DiscountForm({
  preDiscountTotal,
  currentDiscount,
  currentDiscountAmount = 0,
  entityLabel,
  isPending = false,
  onApply,
  onClear,
  onCancel,
}: DiscountFormProps) {
  const [type, setType] = useState<'percent' | 'flat'>(
    currentDiscount?.type ?? 'percent',
  )
  const [value, setValue] = useState(
    currentDiscount?.value != null ? String(currentDiscount.value) : '',
  )

  // One input, one type: normalize through the shared helper so this dialog and
  // the webhook can't disagree about what counts as a discount.
  const discount = normalizeDiscountInput(
    type === 'percent' ? { percent: value } : { flat: value },
  )

  // Always measured against the undiscounted total, which is what makes an edit
  // replace the old discount rather than stack on top of it.
  const preview =
    discount && preDiscountTotal > 0
      ? applyQuoteDiscount({ total: preDiscountTotal, breakdown: [] }, discount)
      : null

  const exceedsTotal =
    preview !== null && !preview.ok && preview.reason === 'exceeds_total'

  // Taking the value to zero (or clearing it) IS the way to remove a discount, so
  // the primary action becomes "Remove discount" rather than going dead. With no
  // discount to remove there is nothing to submit.
  const isRemoval = !discount && !!currentDiscount
  const canSubmit =
    (Boolean(discount) || isRemoval) && !exceedsTotal && !isPending

  // Today's figures, so the operator can see what they're changing from.
  const currentTotal = preDiscountTotal - currentDiscountAmount

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (!canSubmit) return
        if (discount) onApply(discount)
        else if (isRemoval) onClear()
      }}
    >
      <DialogHeader>
        <DialogTitle>
          {currentDiscount ? 'Edit Discount' : 'Apply Discount'}
        </DialogTitle>
        <DialogDescription>
          {currentDiscount
            ? `This ${entityLabel} already has a discount. Change the value or type below, or set it to zero to remove it.`
            : `Reduce this ${entityLabel} by a percentage or a fixed amount.`}
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-4 py-4">
        {currentDiscount && (
          <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm">
            <span className="font-medium text-green-800">
              Currently applied: {discountLabel(currentDiscount)}
            </span>
            <span className="text-green-700">
              −{formatCurrency(currentDiscountAmount)}
            </span>
          </div>
        )}

        <div className="grid gap-2">
          <Label>Discount type</Label>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={type === 'percent' ? 'default' : 'outline'}
              onClick={() => setType('percent')}
            >
              <Percent className="mr-2 h-4 w-4" />
              Percentage
            </Button>
            <Button
              type="button"
              variant={type === 'flat' ? 'default' : 'outline'}
              onClick={() => setType('flat')}
            >
              <DollarSign className="mr-2 h-4 w-4" />
              Fixed amount
            </Button>
          </div>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="discount-value">
            {type === 'percent' ? 'Percentage off' : 'Amount off'}
            {currentDiscount && (
              <span className="text-muted-foreground ml-1 font-normal">
                (0 removes it)
              </span>
            )}
          </Label>
          <div className="flex items-center gap-2">
            {type === 'flat' && (
              <span className="text-muted-foreground">$</span>
            )}
            <Input
              id="discount-value"
              type="number"
              min={type === 'percent' ? 1 : 0.01}
              max={type === 'percent' ? 100 : undefined}
              step={type === 'percent' ? 1 : 0.01}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder={type === 'percent' ? '10' : '50.00'}
              className="w-32"
              autoFocus
            />
            {type === 'percent' && (
              <span className="text-muted-foreground">%</span>
            )}
          </div>
        </div>

        {/* Every figure is derived from the undiscounted total with the same helper
            checkout uses, so the "new total" here is what actually gets charged. */}
        {preDiscountTotal > 0 && (
          <div className="bg-muted space-y-2 rounded-lg p-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Total before discount
              </span>
              <span>{formatCurrency(preDiscountTotal)}</span>
            </div>

            {currentDiscount && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Current total (with{' '}
                  {discountLabel(currentDiscount).toLowerCase()})
                </span>
                <span>{formatCurrency(currentTotal)}</span>
              </div>
            )}

            {preview?.ok && (
              <>
                <div className="flex justify-between border-t pt-2 text-sm">
                  <span className="text-muted-foreground">
                    {currentDiscount ? 'New ' : ''}
                    {discount
                      ? discountLabel(discount).toLowerCase()
                      : 'discount'}
                  </span>
                  <span className="text-green-600">
                    −{formatCurrency(preview.discountAmount)}
                  </span>
                </div>
                <div className="flex justify-between font-medium">
                  <span>
                    {currentDiscount ? 'New total' : 'Discounted total'}
                  </span>
                  <span>{formatCurrency(preview.pricing.total)}</span>
                </div>
              </>
            )}

            {isRemoval && (
              <div className="flex justify-between border-t pt-2 font-medium">
                <span>Total with no discount</span>
                <span>{formatCurrency(preDiscountTotal)}</span>
              </div>
            )}

            {exceedsTotal && (
              <p className="text-destructive text-sm">
                That discount is the same as or more than the total. Enter a
                smaller amount.
              </p>
            )}
          </div>
        )}

        {preDiscountTotal <= 0 && (
          <p className="text-muted-foreground text-sm">
            This {entityLabel} has no price yet. The discount will be stored and
            applied once a price exists.
          </p>
        )}
      </div>

      <DialogFooter className="gap-2 sm:justify-between">
        {/* One-click removal. Hidden once the value is already zero, because the
            submit button has become "Remove discount" and two identical buttons
            would just be a guess for the operator. */}
        {currentDiscount && !isRemoval ? (
          <Button
            type="button"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={onClear}
            disabled={isPending}
          >
            Remove discount
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant={isRemoval ? 'destructive' : 'default'}
            disabled={!canSubmit}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isRemoval
              ? 'Remove discount'
              : currentDiscount
                ? 'Update discount'
                : 'Apply discount'}
          </Button>
        </div>
      </DialogFooter>
    </form>
  )
}
